/**
 * CampaignPost.jsx
 * One post in a campaign week.
 *
 * Editing happens here rather than on the Create screen because the value of a
 * campaign is reading the posts against each other — noticing that Wednesday
 * and Friday make the same point. Sending you to another screen to fix that,
 * seven times, would undo the saving.
 *
 * The full Create screen is still one click away for anything bigger than a
 * wording change.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import MediaPreview from './MediaPreview.jsx';
import { Check, Clock, Edit, Image as ImageIcon, Warning } from './Icons.jsx';
import { formatLondon, toLocalInput } from '../utils/time.js';

const PLATFORM_LABEL = { facebook: 'Facebook', instagram: 'Instagram' };
const LIMITS = { facebook: 5000, instagram: 2200 };

const STATUS = {
  draft: { label: 'Draft', className: 'badge--accent' },
  scheduled: { label: 'Scheduled', className: 'badge--ok' },
  publishing: { label: 'Publishing', className: 'badge--warn' },
  published: { label: 'Published', className: 'badge--ok' },
  failed: { label: 'Failed', className: 'badge--danger' },
  cancelled: { label: 'Cancelled', className: '' },
};

export default function CampaignPost({ index, post, onChanged, onView, onError }) {
  const navigate = useNavigate();

  const [captions, setCaptions] = useState({});
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reloading the campaign replaces the post, so the boxes follow it.
  useEffect(() => {
    setCaptions(Object.fromEntries(post.targets.map((t) => [t.platform, t.caption])));
    setWhen(post.scheduled_at_utc ? toLocalInput(post.scheduled_at_utc) : '');
    setSaved(false);
  }, [post]);

  const locked = post.status !== 'draft';
  const status = STATUS[post.status] ?? { label: post.status, className: '' };

  const dirty =
    post.targets.some((t) => (captions[t.platform] ?? '') !== t.caption) ||
    (post.scheduled_at_utc ? toLocalInput(post.scheduled_at_utc) : '') !== when;

  const tooLong = post.targets.some(
    (t) => (captions[t.platform] ?? '').length > LIMITS[t.platform],
  );

  async function save() {
    setBusy(true);
    try {
      await api.posts.update(post.id, {
        targets: post.targets.map((t) => ({
          platform: t.platform,
          format: t.format ?? 'post',
          caption: captions[t.platform] ?? '',
          media_ids: t.media_id ? [t.media_id] : [],
          first_comment: t.first_comment ?? '',
        })),
      });

      // Moving the slot is an edit, not a schedule: nothing goes out until
      // the week is approved.
      if (when && when !== toLocalInput(post.scheduled_at_utc)) {
        await api.posts.setSlot(post.id, when);
      }

      setSaved(true);
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  const media = post.targets.find((t) => t.media_id);

  return (
    <article className={`wk${locked ? ' wk--locked' : ''}`}>
      <header className="wk__head">
        <span className="wk__index">{index}</span>

        <div className="wk__when">
          {locked ? (
            <span className="wk__time">
              <Clock size={14} /> {formatLondon(post.scheduled_at_utc)}
            </span>
          ) : (
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-label="When this post goes out"
            />
          )}
          <span className="wk__title">{post.notes || 'Untitled post'}</span>
        </div>

        <div className="wk__actions">
          <span className={`badge ${status.className}`}>
            {post.status === 'scheduled' && <Check size={13} />}
            {status.label}
          </span>
          <button
            className="btn btn--sm"
            onClick={() => navigate(`/create?post=${post.id}`)}
            title="Open on the Create screen for media, formats and platforms"
          >
            <Edit size={14} /> Full editor
          </button>
        </div>
      </header>

      <div className="wk__body">
        <div className="wk__media">
          {media ? (
            <MediaPreview
              media={{ id: media.media_id, kind: media.media_kind }}
              className="wk__thumb zoomable"
              onClick={() =>
                onView({
                  id: media.media_id,
                  kind: media.media_kind,
                  width: media.width,
                  height: media.height,
                })
              }
            />
          ) : (
            <div className="wk__thumb wk__thumb--empty">
              <ImageIcon size={22} />
              <span>No image</span>
            </div>
          )}
        </div>

        <div className="wk__captions">
          {post.targets.map((t) => (
            <div className="field" key={t.id}>
              <label htmlFor={`cap-${t.id}`}>
                {PLATFORM_LABEL[t.platform] ?? t.platform}
                <span
                  className={`counter${
                    (captions[t.platform] ?? '').length > LIMITS[t.platform] ? ' over' : ''
                  }`}
                >
                  {(captions[t.platform] ?? '').length} / {LIMITS[t.platform]}
                </span>
              </label>
              <textarea
                id={`cap-${t.id}`}
                // Facebook captions run to paragraphs; Instagram's are a line
                // or two. Sizing them the same clips one and pads the other.
                rows={t.platform === 'facebook' ? 7 : 4}
                readOnly={locked}
                value={captions[t.platform] ?? ''}
                onChange={(e) =>
                  setCaptions((c) => ({ ...c, [t.platform]: e.target.value }))
                }
              />
              {t.first_comment && (
                <p className="hint">First comment: {t.first_comment}</p>
              )}
              {t.last_error && (
                <p className="hint hint--error">
                  <Warning size={13} /> {t.last_error}
                </p>
              )}
            </div>
          ))}

          {!locked && (
            <div className="btn-row">
              <button
                className="btn btn--sm btn--primary"
                onClick={save}
                disabled={busy || !dirty || tooLong}
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              {saved && !dirty && (
                <span className="hint">
                  <Check size={13} /> Saved.
                </span>
              )}
              {tooLong && <span className="hint hint--error">That caption is over the limit.</span>}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
